import {Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BusyService } from '@unleashed/services/common';
import { SignUpUser, User } from '@unleashed/models/account';
import { AuthorizationService } from '@unleashed/services/account';
import { NavigationSteps } from '@unleashed/models/booking';
import { BookingSessionService } from '@unleashed/services/booking';
import { BRAND_OPTIONS, BrandOptions } from '@unleashed/common/config';
import {Subscription} from 'rxjs';

@Component({
  selector: 'ua-login',
  templateUrl: './login-signup.component.html',
  styleUrls: ['./login-signup.component.scss']
})

export class LoginSignupComponent implements OnInit, OnDestroy {

  @Input() showTitle = true;
  @Input() forceSignup = false;
  @Input() hideHeader = false;
  @Input() preventNavigateAway = false;
  @Output() onSuccess: EventEmitter<void> = new EventEmitter();

  emailAddress = '';
  username = '';
  firstName = '';
  lastName = '';
  phoneNumber = '';
  user: User = {loggedIn: false};
  accessToken = '';
  signup = false;
  provisionalAccountId?: number;
  provisionalEmail?: string;
  provisionalEntryWasSignup = false;
  authError?: string;
  brandName: string;

  subscriptions = new Subscription();

  constructor(
    private authService: AuthorizationService,
    private sessionService: BookingSessionService,
    private route: ActivatedRoute,
    private busyService: BusyService,
    @Inject(BRAND_OPTIONS) brandOptions: BrandOptions
  ) {
    this.brandName = brandOptions.name;
  }

  ngOnInit(): void {
    const slug = this.route.snapshot.queryParamMap.get('park');
    const step = this.route.snapshot.queryParamMap.get('step') ?? 'start';
    const existsStep = this.route.snapshot.queryParamMap.get('existsStep') ?? step;
    if (slug) {
      this.sessionService.setParkBySlug(slug);
    }
    this.signup = this.forceSignup || (!!this.route.snapshot.queryParamMap.get('signup') ?? false);


    this.authService.setInProgress(true);

    this.subscriptions.add(
      this.sessionService.getBooking()
        .subscribe (booking => {
          this.emailAddress = booking?.emailAddress ?? '';
          this.firstName = booking?.contactFirstName ??  '';
          this.lastName = booking?.contactLastName ?? '';
          this.phoneNumber = booking?.phoneNumber ?? '';
        })
    );

    this.subscriptions.add(
      this.authService.getUser()
        .subscribe(user => {
          if (user.loggedIn) {
            this.onSuccess.emit();
            if (!this.preventNavigateAway) {
              const key = Object.keys(NavigationSteps).find(ns =>
                ns.toLowerCase() === (this.signup ? step.toLowerCase() : existsStep.toLowerCase()));
              const navStep = NavigationSteps[key as keyof typeof NavigationSteps];
              if (!user.address?.zipCode) {
                this.sessionService.navigateToAccountInfo(navStep, true);
              } else {
                this.sessionService.navigateToStep(navStep);
              }
            }
          }
          this.user = user;
        })
    );

    this.subscriptions.add(
      this.authService.getAccessToken()
        .subscribe(a => this.accessToken = a)
    );

    this.subscriptions.add(
      this.authService.getError()
        .subscribe(error => this.authError = error)
    );
  }

  toggleMode(username: string): void {
    this.username = username;
    this.provisionalAccountId = undefined;
    this.provisionalEmail = undefined;
    this.provisionalEntryWasSignup = false;
    this.authService.reset();
    this.authError = 'We see that you already have an account. Please log in to continue.';
    this.signup = !this.signup;
  }

  startProvisionalFlow(provisional: { accountId: number; email: string }): void {
    this.provisionalEntryWasSignup = this.signup;
    this.provisionalAccountId = provisional.accountId;
    this.provisionalEmail = provisional.email;
    this.username = provisional.email;
    this.emailAddress = provisional.email;
    this.authError = undefined;
    this.authService.reset();
  }

  cancel(): void {
    this.authService.setInProgress(false);
    if (!this.preventNavigateAway) {
      this.sessionService.navigateToStep(NavigationSteps.Start);
    } else {
      this.sessionService.navigateToStep(NavigationSteps.Review);
    }
  }

  cancelLogin(): void {
    if (!this.forceSignup && !this.route.snapshot.queryParamMap.get('signup')) {
      this.cancel();
      return;
    }
    this.emailAddress = '';
    this.signup = true;
    this.provisionalAccountId = undefined;
    this.provisionalEmail = undefined;
    this.provisionalEntryWasSignup = false;
    this.authError = undefined;
  }

  cancelProvisional(): void {
    this.provisionalAccountId = undefined;
    this.provisionalEmail = undefined;
    this.signup = this.provisionalEntryWasSignup;
    this.provisionalEntryWasSignup = false;
    this.authError = undefined;
  }

  createAccount(user: SignUpUser): void {
    this.busyService.set('login', true);
    // [FRAN-289] Do NOT emit onSuccess here. register() logs the user in (authorizeUser -> loadUser),
    // which pushes the logged-in user through the getUser() subscription above, where onSuccess fires
    // once -- and only when the user is actually logged in. Emitting here too made onSuccess fire TWICE
    // on sign-up: the premature one (before the account/booking state settled) drove a second navigate
    // that raced the wizard step-guard and intermittently bounced new-account users to Package Selection
    // instead of Payment. It also fired on a FAILED register (register() yields loggedIn:false on error),
    // which the getUser() path correctly does not.
    this.authService.register(user)
      .subscribe(() => {
        this.busyService.set('login', false);
      });
  }

  ngOnDestroy(): void {
    this.authService.setInProgress(false);
    this.subscriptions.unsubscribe();
  }
}
